import FnrFetch3 from "Utilities/FnrFetch/FnrFetch3";

// Network-scoped, no user id: the surfaces that need this (public signups,
// family registration, giving, sacrament) are often anonymous, so the endpoint
// is Open. See PostAddressVerifyDeliverability (Actions/Address/VerifyDeliverability).
const ENDPOINT = "/address/verifyDeliverability";

/**
 * Ask the backend whether an address is USPS-DELIVERABLE — the same US Street
 * DPV check the save path runs. Used by the optimistic surfaces to gate the
 * "USPS Verified" badge so it can't claim an address the backend would reject
 * (a No-Stat street record in a PO-Box ZIP is a Smarty autocomplete leaf but
 * DPV won't confirm it).
 *
 * `isVerified: "1"` mirrors what the save POSTs (the autocomplete's tentative
 * verdict), so the backend gate reads it as "autocomplete matched a leaf" and
 * returns the authoritative deliverability answer.
 *
 * @param {{addr1?, addr2?, city?, state?, zip?, country?}} address
 * @returns {Promise<boolean|null>} true = deliverable, false = not,
 *   null = couldn't check (outage) → unknown, not a verdict. The caller decides
 *   how to treat unknown; the optimistic-surface badge fails CLOSED (hidden).
 */
export const checkAddressDeliverability = async (address) => {
  const formData = new FormData();
  formData.append("addr1", address?.addr1 ?? "");
  formData.append("addr2", address?.addr2 ?? "");
  formData.append("city", address?.city ?? "");
  formData.append("state", address?.state ?? "");
  formData.append("zip", address?.zip ?? "");
  formData.append("country", address?.country ?? "US");
  formData.append("isVerified", "1");

  try {
    const response = await new FnrFetch3(ENDPOINT, formData).post();
    return response?.isVerified === true;
  } catch {
    // Outage / non-success: unknown, not a verdict. Return null so the caller
    // can distinguish "couldn't check" from a real false; the optimistic-surface
    // badge fails CLOSED on it (we don't assert a verification we didn't
    // confirm). The backend's own check still runs on save.
    return null;
  }
};
